import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../../../contexts/LanguageContext";
import Button from "../../../components/Button";

/**
 * Asks, inline on the card editor, what a changed expiration rule should do to
 * the people who already hold the card.
 *
 * Inline rather than a modal, but it is still a question and not a notice:
 * nothing is saved until it is answered. An earlier version of this banner let
 * the save through first and offered afterwards, which meant the card's rule
 * changed while its cardholders were left untouched with nobody having decided
 * that — and because the banner sits at the top of a long editor while the
 * setting and the save button are at the bottom, it went unread.
 *
 * So the editor holds every save while this is on screen, scrolls it into view,
 * and says as much when a save was held. The two answers are the only way past
 * it; there is no third button that quietly means "save anyway".
 *
 * It only appears when the server says the rule actually changed, which is what
 * makes holding the save safe — a recoloured card never sees it.
 */
export default function ExpirationHoldersBanner({
  preview,
  wasSaveBlocked = false,
  isSaving = false,
  onApplyToExisting,
  onFutureOnly,
}) {
  const { t } = useLanguage();

  if (!preview) return null;

  return (
    <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 shadow-sm ring-2 ring-amber-400/60">
      <div className="flex gap-3">
        <ExclamationTriangleIcon
          className="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          {wasSaveBlocked && (
            <p className="mb-2 text-sm font-semibold text-amber-800">
              {t("passes.expiry.answerFirst")}
            </p>
          )}

          {/* The count lives in `body`, not `title` -- the two were called the
              other way round after the modal was renamed into this banner,
              which rendered the literal "{count}" to the merchant. */}
          <p className="text-sm font-medium text-gray-900">
            {t("passes.expiry.title")}
          </p>
          <p className="mt-1 text-sm text-gray-700">
            {t("passes.expiry.body", { count: preview.total })}
          </p>

          <ul className="mt-2 space-y-1 text-sm text-gray-700">
            <li>{t("passes.expiry.willUpdate", { count: preview.will_update })}</li>
            {preview.will_reactivate > 0 && (
              <li>
                {t("passes.expiry.willReactivate", { count: preview.will_reactivate })}
              </li>
            )}
            {preview.will_expire_immediately > 0 && (
              <li className="font-medium text-red-700">
                {t("passes.expiry.willExpire", {
                  count: preview.will_expire_immediately,
                })}
              </li>
            )}
          </ul>

          <p className="mt-2 text-sm text-gray-600">
            {t("passes.expiry.overwriteWarning")}
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={onApplyToExisting} disabled={isSaving}>
              {t("passes.expiry.apply")}
            </Button>
            <Button variant="secondary" onClick={onFutureOnly} disabled={isSaving}>
              {t("passes.expiry.futureOnly")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
